import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "../ui/button";
import { getMetaConsent, setMetaConsent, subscribeMetaConsent } from "../../utils/metaConsent";
import { isPublicMetaPath, trackMetaPageView } from "../../utils/metaPixel";

const CookieContext = createContext(null);
export const useCookiePreferences = () => useContext(CookieContext);
const choiceClass = "h-11 flex-1 border border-white/30 bg-charcoal px-6 font-display font-semibold text-white transition-colors hover:border-amber hover:bg-elevated focus-visible:ring-amber";

export const CookieConsentProvider = ({ children }) => {
  const { pathname } = useLocation();
  const choice = useSyncExternalStore(subscribeMetaConsent, getMetaConsent, () => null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [storageWarning, setStorageWarning] = useState(false);
  const returnFocus = useRef(null);
  const rejectButton = useRef(null);
  const visible = isPublicMetaPath(pathname) && (!choice || preferencesOpen);

  useEffect(() => { trackMetaPageView(pathname); }, [pathname, choice]);
  useEffect(() => {
    if (preferencesOpen && visible) rejectButton.current?.focus({ preventScroll: true });
  }, [preferencesOpen, visible]);

  const closePreferences = () => {
    setPreferencesOpen(false);
    returnFocus.current?.focus({ preventScroll: true });
  };
  const choose = (value) => {
    setStorageWarning(!setMetaConsent(value));
    closePreferences();
  };

  return (
    <CookieContext.Provider value={{ openPreferences: () => {
      returnFocus.current = document.activeElement;
      setPreferencesOpen(true);
    } }}>
      {children}
      {storageWarning && (
        <p role="status" data-testid="cookie-storage-warning" className="fixed inset-x-4 bottom-4 z-[81] rounded-lg border border-white/20 bg-charcoal p-4 text-sm text-white">
          Your cookie choice applies to this visit. Your browser could not save it for future visits.
          <button type="button" data-testid="cookie-storage-warning-dismiss" aria-label="Dismiss cookie storage notice" className="ml-3 inline-flex p-1" onClick={() => setStorageWarning(false)}><X size={16} /></button>
        </p>
      )}
      {visible && (
        <section
          role="region"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-description"
          data-testid="cookie-consent-banner"
          className="fixed inset-x-0 bottom-0 z-[80] border-t border-white/20 bg-obsidian p-5 shadow-[0_-12px_48px_rgba(0,0,0,0.4)] sm:p-6"
          onKeyDown={(event) => { if (event.key === "Escape" && choice) closePreferences(); }}
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-5 lg:flex-row lg:items-center lg:gap-12">
            <div className="min-w-0 flex-1">
              <h2 id="cookie-consent-title" data-testid="cookie-consent-title" className="flex items-center gap-2 pr-8 font-display text-base font-semibold text-white md:text-lg"><Cookie size={19} className="text-amber" /> Cookie preferences</h2>
              <p id="cookie-consent-description" data-testid="cookie-consent-description" className="mt-2 max-w-3xl text-sm leading-relaxed text-white/75">
                With your permission, we use Meta Pixel advertising cookies to measure page visits and successful quote requests. We do not send your quote details to Meta. Rejecting does not affect your enquiry. Change or withdraw consent anytime using Cookie preferences in the footer.
              </p>
              <p data-testid="cookie-consent-status" className="mt-2 text-xs text-white/60">
                {choice === "accepted" ? "Meta tracking: accepted. Choose Reject to withdraw consent." : choice === "rejected" ? "Meta tracking: rejected." : "Meta tracking is off until you accept."}
              </p>
            </div>
            <div className="flex w-full gap-3 lg:w-72 lg:shrink-0">
              <Button ref={rejectButton} type="button" data-testid="cookie-reject-button" className={choiceClass} onClick={() => choose("rejected")}>Reject</Button>
              <Button type="button" data-testid="cookie-accept-button" className={choiceClass} onClick={() => choose("accepted")}>Accept</Button>
            </div>
          </div>
          {choice && <button type="button" data-testid="cookie-preferences-close" aria-label="Close cookie preferences without changing your choice" className="absolute right-4 top-4 rounded p-2 text-white/75 transition-colors hover:text-amber focus-visible:outline focus-visible:outline-amber" onClick={closePreferences}><X size={18} /></button>}
        </section>
      )}
    </CookieContext.Provider>
  );
};